/**
 * RUNNER public API schemas. Large integers are DECIMAL STRINGS. Every response carries `source`
 * (block, hash, finality, indexedAt) so clients can show freshness instead of inventing it.
 */
import { z } from 'zod';

export const Dec = z.string().regex(/^-?\d+$/, 'decimal integer string');
export const Addr = z.string().regex(/^0x[0-9a-fA-F]{40}$/);
export const Hash = z.string().regex(/^0x[0-9a-fA-F]{64}$/);

/** Integration status tags (spec §18). Never conflate them. */
export const IntegrationStatus = z.enum([
  'implemented',
  'verified locally',
  'verified on fork',
  'verified on testnet',
  'verified on mainnet',
  'externally blocked',
  'future work',
]);

export const Finality = z.enum(['pending', 'latest', 'safe', 'finalized']);

/**
 * Quote assets the API may mark `verified` off the local chain. The deployment's quote address must match AND the
 * token's on-chain symbol and decimals (read live by the API) must equal these. Addresses are owner-reported from
 * Robinhood's token-contracts page (D55, D62); the label says exactly what was checked.
 */
export const KNOWN_QUOTE_ASSETS: Readonly<Record<number, readonly { address: string; symbol: string; decimals: number; label: string }[]>> = {
  4663: [
    {
      address: '0x5fc5360D0400a0Fd4f2af552ADD042D716F1d168',
      symbol: 'USDG',
      decimals: 6,
      label: "USDG (Global Dollar) — address from Robinhood's token-contracts page; symbol and decimals checked on chain. Its issuer can pause transfers or freeze addresses.",
    },
    {
      address: '0x0Bd7D308f8E1639FAb988df18A8011f41EAcAD73',
      symbol: 'WETH',
      decimals: 18,
      label: "WETH — address from Robinhood's token-contracts page; symbol and decimals checked on chain.",
    },
  ],
};

/** The known-asset label when (chain, address, on-chain symbol, on-chain decimals) all match; otherwise null. */
export function knownQuoteAsset(chainId: number, address: string, symbol: string, decimals: number): string | null {
  const k = (KNOWN_QUOTE_ASSETS[chainId] ?? []).find((q) => q.address.toLowerCase() === address.toLowerCase());
  return k && k.symbol === symbol && k.decimals === decimals ? k.label : null;
}

export const Source = z.object({
  chainId: z.number(),
  blockNumber: Dec,
  blockHash: Hash.nullable(),
  finality: Finality,
  indexedAt: z.string(),
  lagBlocks: z.number().nullable(),
  stale: z.boolean(),
});
export type Source = z.infer<typeof Source>;

export const Mode = z.enum(['local', 'fork', 'testnet', 'mainnet']);

export const AssetInfo = z.object({
  address: Addr,
  symbol: z.string(),
  name: z.string(),
  decimals: z.number(),
  verified: z.boolean(),
  label: z.string(), // e.g. "LOCAL TEST ASSET — not a stock token"
});

export const Deployment = z.object({
  mode: Mode,
  chainId: z.number(),
  network: z.string(),
  warning: z.string().optional(),
  contracts: z.record(z.string(), Addr),
  poolId: Hash,
  /** The hook fee now (read from the hook); governance may change it after its delay (D61). */
  hookFeeBps: z.number(),
  /** Highest fee governance may set (D61), fixed at deployment. */
  maxHookFeeBps: z.number().optional(),
  /** Anti-sniping launch fee on buys (D57): bps at genesis, falling linearly to hookFeeBps over launchFeeSeconds. */
  launchFeeBps: z.number().optional(),
  launchFeeSeconds: z.number().optional(),
  lpFee: z.number(),
  tickSpacing: z.number(),
  genesis: z.number(),
  windowSeconds: z.number(),
  epochSeconds: z.number(),
  referenceSupply: Dec,
  runner: AssetInfo,
  quote: AssetInfo,
  integrations: z.array(z.object({ name: z.string(), status: IntegrationStatus, detail: z.string() })),
});
export type Deployment = z.infer<typeof Deployment>;

export const CourseObject = z.object({
  index: z.number(),
  kind: z.number(),
  kindName: z.string(),
  y: z.number(),
  xL: z.number(),
  xR: z.number(),
  funded: z.boolean(),
});
export type CourseObjectDto = z.infer<typeof CourseObject>;

export const RunnerStateDto = z.object({
  x: z.number(),
  y: z.number(),
  vx: z.number(),
  pressure: z.number(),
  route: z.number(),
  inSplit: z.boolean(),
  slowPct: z.number(),
  slowSteps: z.number(),
  overheatSteps: z.number(),
  stumbleUntilY: z.number(),
});
export type RunnerStateDto = z.infer<typeof RunnerStateDto>;

export const EncounterDto = z.object({
  objectIndex: z.number(),
  kind: z.number(),
  kindName: z.string(),
  hit: z.boolean(),
  xCross: z.number(),
  amount: Dec,
});

export const StepDto = z.object({
  runId: z.number(),
  step: z.number(),
  window: z.number(),
  net: Dec,
  volume: Dec,
  u: z.number(),
  pace: z.number(),
  state: RunnerStateDto,
  stateHash: Hash,
  encounters: z.array(EncounterDto),
  txHash: Hash,
  blockNumber: Dec,
  timestamp: z.number(),
});
export type StepDto = z.infer<typeof StepDto>;

export const RunStatus = z.enum(['None', 'CourseRequested', 'Active', 'Finished']);
export const RouteName = z.enum(['neutral', 'forge', 'harvest']);

/** RunnerTreasury escrow of one run's reserved road budget (raw quote units). */
export const RunEscrowDto = z.object({
  open: z.boolean(),
  cohortEpoch: z.number(),
  policyId: z.number(),
  /** Reserved from the road reserve at course request; immutable afterwards. */
  budget: Dec,
  assigned: Dec,
  toHolders: Dec,
  toForge: Dec,
  /** budget − assigned while open; 0 once closed. */
  unassigned: Dec,
  /** After close: what went back to the road reserve (budget − assigned). Null while open. */
  returnedToRoad: Dec.nullable(),
  /** Execution provision moved from operations to the execution reserve at funding. */
  provision: Dec,
});
export type RunEscrowDto = z.infer<typeof RunEscrowDto>;

export const EngineRefDto = z.object({ address: Addr, version: z.number() });

export const RunDto = z.object({
  runId: z.number(),
  status: RunStatus,
  /** Closed without playing because course randomness never arrived (budget returned to the road reserve). */
  abandoned: z.boolean(),
  configVersion: z.number(),
  /** Engine pinned at funding (null only for a run that was never funded). */
  engine: EngineRefDto.nullable(),
  /** Sealed holder cohort (last completed reward epoch at funding). */
  cohortEpoch: z.number(),
  /** Waterfall policy in force when the run was funded. */
  policyId: z.number(),
  budget: Dec,
  /**
   * Exhibition run (D48): requested with budget 0 because the road reserve could not fund it under a configuration with
   * minRunBudget = 0. It plays like any run, but no road budget is at stake (road effects assign nothing).
   */
  exhibition: z.boolean(),
  fundedAt: z.number(),
  /** Route at close (neutral/forge/harvest); null until the run is finished. */
  exitRoute: RouteName.nullable(),
  /** True when the FINISH line was crossed (exit allocation applied); false at the step bound; null if unknown/not finished. */
  reachedFinish: z.boolean().nullable(),
  startWindow: z.number(),
  stepsResolved: z.number(),
  maxSteps: z.number(),
  courseLength: z.number(),
  seed: Hash.nullable(),
  courseHash: Hash.nullable(),
  requestId: Dec,
  shoeId: Dec,
  course: z.array(CourseObject),
  requestedAt: z.number(),
  startedAt: z.number(),
  finishedAt: z.number(),
  escrow: RunEscrowDto,
  /** Funding / close provenance from the indexed RunFunded / RunClosed events (null until indexed). */
  provenance: z.object({
    fundedTx: Hash.nullable(),
    fundedBlock: Dec.nullable(),
    roadReserveAfterFunding: Dec.nullable(),
    closedTx: Hash.nullable(),
    closedBlock: Dec.nullable(),
  }),
});
export type RunDto = z.infer<typeof RunDto>;

/**
 * Player-facing phase (docs/GAME_SPEC.md): warm-up (no funded run in flight and the next run is not fundable; replays
 * stay available) → funding (course requested: budget reserved — or none for an exhibition run (D48) — waiting for
 * randomness / start) → preview (course
 * revealed, first steering window not open) → running (steering windows open / resolving) → finished (result, held
 * for `resultHoldSeconds` of chain time after the close) → replay (no run in flight, the next run is fundable but not
 * yet requested; the last run's replay is shown).
 */
export const Phase = z.enum(['warm-up', 'funding', 'preview', 'running', 'finished', 'replay']);
/** Protocol stage behind the phase (what the next transition waits for). */
export const Stage = z.enum([
  'no-run', // nothing ever requested
  'awaiting-randomness', // funded, provider has not fulfilled
  'randomness-ready', // fulfilled; the permissionless startRun has not been mined
  'abandonable', // anyone may abandonRun: randomness missing ABANDON_AFTER after funding (whole budget returns), or an
  // active run unfinished STALL_GRACE after its last possible step (assigned value stays, the unassigned rest returns)
  'preview',
  'input-open', // current window accepting trades for the next step
  'catch-up', // closed windows awaiting permissionless advance()
  'result', // finished, within the result hold
  'awaiting-next-run', // finished/abandoned; the next requestCourse has not been mined
]);

export const TimingDto = z.object({
  now: z.number(),
  currentWindow: z.number(),
  windowSeconds: z.number(),
  windowEndsAt: z.number(),
  nextStepWindow: z.number().nullable(),
  unresolvedClosedWindows: z.number(),
  phase: Phase,
  stage: Stage,
  epoch: z.number(),
  epochEndsAt: z.number(),
  /** Protocol timestamp a countdown may target; null when no honest countdown exists (e.g. awaiting randomness). */
  countdownTo: z.number().nullable(),
  /** Finer-grained explanation (e.g. 'road-reserve-below-minimum', 'window-12-feeds-step-0'). */
  phaseDetail: z.string(),
  /** Chain time from which abandonRun() is possible (CourseRequested only; null otherwise). */
  abandonableAt: z.number().nullable(),
});
export type TimingDto = z.infer<typeof TimingDto>;

export const WindowInputDto = z.object({ window: z.number(), net: Dec, volume: Dec, swaps: z.number() });

export const FundingReason = z.enum(['fundable', 'no-active-config', 'funding-paused', 'road-reserve-below-minimum', 'operations-below-provision']);

/** Whether requestCourse would fund the next run now (mirror of RunnerTreasury.reserveRun at the pinned block). */
export const FundingDto = z.object({
  fundable: z.boolean(),
  /**
   * Not fundable, but the active configuration has minRunBudget = 0: requestCourse would start an exhibition run
   * (budget 0, no road budget at stake; D48) instead of reverting NotFunded. `reason` says why it is unfunded.
   */
  exhibition: z.boolean(),
  reason: FundingReason,
  configVersion: z.number(),
  /** Budget the next run would reserve now (0 when not fundable). Settlement inside requestCourse can only add to it. */
  nextBudget: Dec,
  roadReserve: Dec,
  operations: Dec,
  runBudget: Dec,
  minRunBudget: Dec,
  opsProvision: Dec,
  fundingPaused: z.boolean(),
});
export type FundingDto = z.infer<typeof FundingDto>;

export const RoadParamsDto = z.object({
  runBudget: Dec,
  minRunBudget: Dec,
  opsProvision: Dec,
  waterSliceBps: z.number(),
  overheatSliceBps: z.number(),
  overheatBiasSteps: z.number(),
  overheatCap: Dec,
});
export type RoadParamsDto = z.infer<typeof RoadParamsDto>;

export const RoadEffectName = z.enum(['water', 'overheat', 'exit-holders', 'exit-forge']);

/** One RunnerGame.RoadEffect: an assignment of the run's unassigned budget (indexed). */
export const RoadEffectDto = z.object({
  step: z.number(),
  effect: z.number(),
  effectName: RoadEffectName,
  to: z.enum(['holders', 'forge']),
  requested: Dec,
  assigned: Dec,
  txHash: Hash,
  blockNumber: Dec,
});
export type RoadEffectDto = z.infer<typeof RoadEffectDto>;

/** The live (or last) run's road budget versus what it has assigned. */
export const RoadDto = z.object({
  runId: z.number().nullable(),
  escrow: RunEscrowDto.nullable(),
  /** Road parameters of the run's pinned configuration (the active configuration when there is no run). */
  params: RoadParamsDto,
  /** What one Water hit assigns to holders for this run (waterSliceBps × budget, before clipping). */
  waterSlice: Dec,
  /** What one Overheat-biased step assigns to Forge for this run (overheatSliceBps × budget, before clipping). */
  overheatPerStep: Dec,
  effects: z.array(RoadEffectDto),
});
export type RoadDto = z.infer<typeof RoadDto>;

/** RunnerGame Overheat bias (one at a time). */
export const OverheatDto = z.object({
  active: z.boolean(),
  modifierId: z.number().nullable(),
  stepsLeft: z.number(),
  /** Assigned to Forge by the live bias so far. */
  assigned: Dec,
  cap: Dec,
  sliceBps: z.number(),
  perStep: Dec,
  /** Global counter of biases ever started. */
  modifierCount: z.number(),
});
export type OverheatDto = z.infer<typeof OverheatDto>;

export const WaterfallPolicyDto = z.object({
  policyId: z.number(),
  fromEpoch: z.number(),
  holdersBps: z.number(),
  forgeBps: z.number(),
  roadBps: z.number(),
  prizeBps: z.number(),
  opsBps: z.number(),
  holderDeferredBps: z.number(),
});
export type WaterfallPolicyDto = z.infer<typeof WaterfallPolicyDto>;

export const WaterfallSplitDto = z.object({
  holdersDirect: Dec,
  holdersDeferred: Dec,
  forge: Dec,
  road: Dec,
  prize: Dec,
  operations: Dec,
});
export type WaterfallSplitDto = z.infer<typeof WaterfallSplitDto>;

/** Base holder income is independent of runs; shown next to the run's road budget. */
export const BaseRewardsDto = z.object({
  epoch: z.number(),
  policy: WaterfallPolicyDto,
  /** Fees accrued in the open accrual period (a receivable until redeemed and settled). */
  openPeriodAccrued: Dec,
  /** waterfallSplit(openPeriodAccrued) under the epoch's policy — an ESTIMATE of this epoch's base allocation. */
  openPeriodSplitEstimate: WaterfallSplitDto,
  /** Base holder credit of the run's sealed cohort epoch (indexed EpochCredited), when credited. */
  cohortEpoch: z.number().nullable(),
  cohortHolderCredit: z.object({ direct: Dec, deferred: Dec, weight: Dec.nullable(), carried: z.boolean() }).nullable(),
  note: z.string(),
});
export type BaseRewardsDto = z.infer<typeof BaseRewardsDto>;

/** The sealed holder cohort the current run credits (RunnerTreasury + RunnerRewards views). */
export const CohortDto = z.object({
  epoch: z.number(),
  sealed: z.literal(true),
  /** Sealed global weight of the cohort epoch (RunnerRewards.globalWeightAt). */
  globalWeight: Dec,
  /** Holder-directed road value of THIS cohort not yet credited to RunnerRewards (0 once credited). */
  pendingRoadValue: Dec,
  /** Runs bound to this cohort that are still open. */
  openRuns: z.number(),
  /** Run-cohort credits for epochs before this count are final (claimable). */
  runFinalizedCount: z.number(),
});
export type CohortDto = z.infer<typeof CohortDto>;

export const ShoeDto = z.object({
  shoeId: Dec,
  runId: z.number(),
  objectIndex: z.number(),
  status: z.enum(['None', 'Open', 'Sealed', 'Collected', 'Missed', 'Drawing', 'Claimable', 'Unawarded']),
  amount: Dec,
  cutoff: z.number(),
  totalWeight: Dec,
  entryCount: z.number(),
  winner: Addr.nullable(),
  requestId: Dec,
});
export type ShoeDto = z.infer<typeof ShoeDto>;

export const BuybackDto = z.object({
  openAuthorized: Dec,
  inventory: Dec,
  totalQuoteSpent: Dec,
  totalPurchased: Dec,
  totalBurned: Dec,
  paused: z.boolean(),
  authorizations: z.array(
    z.object({ id: z.number(), amount: Dec, spent: Dec, createdAt: z.number(), deadline: z.number(), closed: z.boolean() }),
  ),
});

export const GameStateDto = z.object({
  source: Source,
  run: RunDto.nullable(),
  state: RunnerStateDto,
  lastSteps: z.array(StepDto),
  openWindow: WindowInputDto.nullable(),
  timing: TimingDto,
  funding: FundingDto,
  road: RoadDto,
  overheat: OverheatDto,
  baseRewards: BaseRewardsDto,
  cohort: CohortDto.nullable(),
  shoe: ShoeDto.nullable(),
  fundingPaused: z.boolean(),
  /** Last finished (not abandoned) run whose replay is available (GET /v1/verify/run/:id, /v1/runs/:id/steps). */
  replayRunId: z.number().nullable(),
});
export type GameStateDto = z.infer<typeof GameStateDto>;

export const MarketDto = z.object({
  source: Source,
  /** Shared supply read; optional for rolling API/frontend upgrades. */
  runnerSupply: Dec.optional(),
  sqrtPriceX96: Dec,
  tick: z.number(),
  liquidity: Dec,
  priceQuotePerRunner: z.string(), // decimal string, display only
  /** The hook fee at this block (governance-set, D61). */
  hookFeeBps: z.number(),
  /** The rate a buy pays at this block (above hookFeeBps only during the launch window, D57). */
  buyFeeBps: z.number().optional(),
  lpFeePips: z.number(),
  routes: z.array(
    z.object({
      route: z.string(),
      trading: IntegrationStatus,
      steersGame: z.boolean(),
      paysProjectFee: z.boolean(),
      goldenShoeEligible: z.boolean(),
      note: z.string(),
    }),
  ),
});

export const QuoteDto = z.object({
  source: Source,
  side: z.enum(['buy', 'sell']),
  exactInput: z.boolean(),
  amountIn: Dec,
  amountOut: Dec,
  projectFee: Dec,
  /** Rate applied to this quote: the current buy rate for buys (launch fee window, D57), the base fee for sells. */
  projectFeeBps: z.number().optional(),
  lpFeeEstimate: Dec,
  priceImpactBps: z.number(),
  expiresAt: z.number(),
  goldenShoeEligible: z.boolean(),
  route: z.string(),
});

/** One ledger, three streams (RunnerRewards). Identity-backed principal is PART of `principal.total`, never added to it. */
export const RewardsAccountDto = z.object({
  source: Source,
  account: Addr,
  /** Where claims are paid (RunnerRewards.payoutOf, defaulting to the account). */
  payout: Addr,
  wallet: z.object({ runner: Dec, quote: Dec }),
  principal: z.object({
    /** Ordinary + identity-backed principal (RunnerRewards.depositOf). */
    total: Dec,
    eligibleNow: Dec,
    pendingNow: Dec,
    /** total − identityBacked. */
    ordinary: Dec,
    ordinaryEligible: Dec,
    ordinaryPending: Dec,
    /** Backing of the Runners this account owns (RunnerRewards.identityPrincipalOf) — included in `total`. */
    identityBacked: Dec,
    identityEligible: Dec,
    identityPending: Dec,
  }),
  claimable: z.object({ direct: Dec, deferred: Dec, run: Dec, total: Dec }),
  /** Deferred income of finalized epochs not yet claimable (awaiting a release or the deadline). Estimate. */
  lockedDeferredEstimate: Dec,
  unpaid: Dec,
  prizeOwed: Dec,
  prizePaid: Dec,
  identities: z.array(z.object({ tokenId: Dec, backing: Dec, eligibleFrom: z.number(), eligibleNow: z.boolean() })),
  epochs: z.object({ current: z.number(), finalized: z.number(), runFinalized: z.number(), deferredClaimable: z.number() }),
  globalWeightCurrentEpoch: Dec,
  recentClaims: z.array(z.object({ direct: Dec, deferred: Dec, run: Dec, to: Addr, failed: z.boolean(), txHash: Hash, blockNumber: Dec, at: z.number() })),
});
export type RewardsAccountDto = z.infer<typeof RewardsAccountDto>;

/** The three income streams of the one ledger (RunnerRewards DIRECT / DEFERRED / RUN). */
export const RewardStream = z.enum(['direct', 'deferred', 'run']);
export type RewardStream = z.infer<typeof RewardStream>;

/**
 * Status of one stream of one epoch for one account, from the vault's claim cursors and gates at the pinned block:
 * `claimed` (before the account's cursor: paid or recorded unpaid by an earlier claim), `claimable` (claimable now),
 * `locked` (deferred income of a finalized epoch awaiting a Water/finish release or the deadline) or `not-final`
 * (credited, but the epoch is not finalized for this stream yet).
 */
export const RewardEpochStatus = z.enum(['claimed', 'claimable', 'locked', 'not-final']);
export type RewardEpochStatus = z.infer<typeof RewardEpochStatus>;

export const RewardsBreakdownStreamDto = z.object({
  stream: RewardStream,
  /** Income credited to the epoch for ALL holders (indexed EpochCredited / RunCohortCredited; includes `carriedIn`). */
  credited: Dec,
  /** Part of `credited` carried in from earlier epochs that had no eligible weight (indexed EpochCarried / RunCohortCarried). */
  carriedIn: Dec,
  /** Sealed global weight the credit was shared by. */
  globalWeight: Dec,
  /** Credit per unit of weight as the vault stored it: ⌊credited × perWeightScale / globalWeight⌋. */
  perWeight: Dec,
  /** This account's amount (vault rounding: ⌊weight × Δcum / scale⌋ per claim range, split per epoch exactly). */
  amount: Dec,
  status: RewardEpochStatus,
  creditTx: Hash,
  creditBlock: Dec,
});

/** Share of an epoch's amount backed by one Runner identity (⌊amount × backing / weight⌋ per stream). */
export const RewardsRunnerShareDto = z.object({ tokenId: Dec, backing: Dec, direct: Dec, deferred: Dec, run: Dec, amount: Dec });

export const RewardsBreakdownEpochDto = z.object({
  epoch: z.number(),
  /** The account's recorded (sealed) weight in this epoch: principal held from before the epoch through all of it. */
  weight: Dec,
  /** Backing of the Runners that counted for this account in this epoch (eligibleFrom ≤ epoch < end of holding). */
  identityWeight: Dec,
  ordinaryWeight: Dec,
  streams: z.array(RewardsBreakdownStreamDto),
  amount: Dec,
  /** Amount not yet claimed (claimable + locked + not-final streams). */
  unclaimed: Dec,
  runners: z.array(RewardsRunnerShareDto),
  /** amount − Σ runners[].amount: the ordinary part (plus at most a few base units of per-token rounding). */
  ordinaryAmount: Dec,
});
export type RewardsBreakdownEpochDto = z.infer<typeof RewardsBreakdownEpochDto>;

/** One Runner this account holds or held, with what its backing earned for THIS account (former Runners included). */
export const RewardsBreakdownRunnerDto = z.object({
  tokenId: Dec,
  backing: Dec,
  /** From this account's point of view: still held, or how the holding ended. */
  status: z.enum(['held', 'transferred', 'redeemed', 'converted']),
  /** Holding periods of this account: [eligibleFrom, until) in epochs; `until` null while held. */
  periods: z.array(z.object({ eligibleFrom: z.number(), until: z.number().nullable(), endedBy: z.enum(['transferred', 'redeemed', 'converted']).nullable() })),
  /** Credited epochs in which this Runner's backing counted for the account. */
  epochs: z.array(z.number()),
  direct: Dec,
  deferred: Dec,
  run: Dec,
  amount: Dec,
  claimable: Dec,
  unclaimed: Dec,
});
export type RewardsBreakdownRunnerDto = z.infer<typeof RewardsBreakdownRunnerDto>;

const StreamTotalsDto = z.object({ direct: Dec, deferred: Dec, run: Dec, total: Dec });

/**
 * GET /v1/rewards/:account/breakdown. Entitlements per epoch and per Runner reconstructed from indexed vault events
 * (history and attribution), cross-checked against RunnerRewards.claimable read at the SAME pinned block. `claimable`
 * is always the on-chain value; when the reconstruction does not reproduce it (indexer behind, reorg, missing
 * events) `complete` is false and `reason` says why — the rows are then partial history, never a reconciliation.
 */
export const RewardsBreakdownResponse = z.object({
  source: Source,
  account: Addr,
  complete: z.boolean(),
  reason: z.string().nullable(),
  /** Highest block the indexer had processed when this response was built (null: nothing indexed). */
  indexedHead: Dec.nullable(),
  /**
   * Which block the contract reads are pinned to: `indexed-head` when the index is behind the chain head (for example
   * INDEXER_CONFIRMATIONS > 0) and the chain still has that exact block; otherwise `latest`. `source.blockNumber` is it.
   */
  pinnedTo: z.enum(['latest', 'indexed-head']),
  /** RunnerRewards.claimable(account, unbounded segments) at the pinned block: the financial truth. */
  claimable: StreamTotalsDto,
  /** Σ breakdown streams with status `claimable`. Equal to `claimable` iff `complete` (with the checks below). */
  breakdownClaimable: StreamTotalsDto,
  /** Σ breakdown streams not claimable yet: deferred awaiting release (`locked`) and credited but not final. */
  notYetClaimable: z.object({ locked: Dec, notFinal: Dec }),
  /** Recomputed amounts of claimed epochs (±1 base unit per past claim range; payouts are the Claimed events). */
  claimedRecomputed: StreamTotalsDto,
  unpaid: Dec,
  checks: z.object({
    /** The indexer had processed the pinned block. */
    indexCoversPinnedBlock: z.boolean(),
    /** Reconstructed weight segments equal RunnerRewards.segments (+ the pending roll) at the pinned block. */
    segmentsMatch: z.boolean(),
    /** breakdownClaimable equals claimable for every stream. */
    claimableMatches: z.boolean(),
  }),
  vault: z.object({
    currentEpoch: z.number(),
    finalizedCount: z.number(),
    runFinalizedCount: z.number(),
    deferredClaimableCount: z.number(),
    directNext: z.number(),
    deferredNext: z.number(),
    runNext: z.number(),
  }),
  /** RunnerRewards.RPW_SCALE: `perWeight` values are scaled by it. */
  perWeightScale: Dec,
  /** Newest first, paged by `before` (exclusive epoch) / `limit`. Totals above always cover every epoch. */
  epochs: z.array(RewardsBreakdownEpochDto),
  epochCount: z.number(),
  nextCursor: z.number().nullable(),
  runners: z.array(RewardsBreakdownRunnerDto),
  note: z.string(),
});
export type RewardsBreakdownResponse = z.infer<typeof RewardsBreakdownResponse>;

export const ProposalDto = z.object({
  id: z.number(),
  kind: z.string(),
  status: z.string(),
  proposer: Addr,
  approvals: z.number(),
  createdAt: z.number(),
  eta: z.number(),
  executedAt: z.number(),
  payloadHash: Hash,
  rationaleHash: Hash,
  simulationHash: Hash,
  uri: z.string(),
  result: Dec,
});

export const JournalEntryDto = z.object({
  id: z.string(),
  at: z.number(),
  kind: z.enum(['release', 'proposal', 'run', 'encounter', 'prize', 'buyback', 'burn', 'rewards', 'operations']),
  title: z.string(),
  body: z.string(),
  evidence: z.array(z.object({ label: z.string(), txHash: Hash.optional(), blockNumber: Dec.optional(), url: z.string().optional() })),
  contentHash: Hash,
  onchainCommitment: z.object({ entryId: z.number(), txHash: Hash }).nullable(),
  author: z.enum(['deterministic-template', 'agent-draft-approved', 'operator']),
});
export type JournalEntryDto = z.infer<typeof JournalEntryDto>;

export const FeedEventDto = z.object({
  seq: z.number(),
  type: z.string(),
  at: z.number(),
  blockNumber: Dec,
  txHash: Hash,
  logIndex: z.number(),
  finality: Finality,
  summary: z.string(),
  data: z.record(z.string(), z.unknown()),
});
export type FeedEventDto = z.infer<typeof FeedEventDto>;

export const ReplayDto = z.object({
  runId: z.number(),
  engine: z.string(),
  steps: z.number(),
  matched: z.number(),
  mismatches: z.array(z.object({ step: z.number(), expected: Hash, actual: Hash })),
  courseHashMatches: z.boolean(),
  seed: Hash.nullable(),
});

// =====================================================================================================
// Services API (apps/api, services/indexer, services/executor). Change order 4.0 replaced the v3 economy DTOs
// (allocation versions, economic coordinate, Overheat shift) with run funding, road budgets, the five-bucket
// waterfall, identities and versions. The wrappers below add `source` where the base DTO has none.
// =====================================================================================================

/** Versioned live-feed event types (GET /v1/events, GET /v1/stream). A breaking payload change gets a new `.vN`. */
export const FEED_EVENT_TYPES = [
  'chain.reorg.v1',
  'run.course-requested.v1',
  'run.funded.v1',
  'run.randomness-retried.v1',
  'run.started.v1',
  'run.step.v1',
  'run.encounter.v1',
  'run.finished.v1',
  'run.closed.v1',
  'run.abandoned.v1',
  'road.effect.v1',
  'road.overheat-started.v1',
  'road.overheat-ended.v1',
  'market.swap.v1',
  'hook.fee-changed.v1',
  'treasury.redeemed.v1',
  'treasury.policy-scheduled.v1',
  'treasury.period-settled.v2',
  'treasury.epoch-flushed.v1',
  'treasury.holder-credit-queued.v1',
  'treasury.epochs-finalized.v1',
  'treasury.cohort-credited.v1',
  'treasury.run-epochs-finalized.v1',
  'treasury.prize-reserved.v1',
  'treasury.prize-returned.v1',
  'treasury.forge-authorized.v1',
  'treasury.forge-returned.v1',
  'treasury.lp-income.v1',
  'treasury.donation.v1',
  'treasury.operations-spent.v1',
  'treasury.ops-config.v1',
  'rewards.deposited.v1',
  'rewards.withdrawn.v1',
  'rewards.epoch-credited.v1',
  'rewards.epoch-carried.v1',
  'rewards.run-cohort-credited.v1',
  'rewards.run-cohort-carried.v1',
  'rewards.finalized.v1',
  'rewards.run-finalized.v1',
  'rewards.deferred-released.v1',
  'rewards.claimed.v1',
  'rewards.claim-failed.v1',
  'rewards.payout-set.v1',
  'identity.minted.v1',
  'identity.transferred.v1',
  'identity.closed.v1',
  'identity.intake.v1',
  'identity.renderer-set.v1',
  'wardrobe.item-registered.v1',
  'wardrobe.equipped.v1',
  'prize.shoe-opened.v1',
  'prize.shoe-unfunded.v1',
  'prize.entry.v1',
  'prize.shoe-sealed.v1',
  'prize.shoe-outcome.v1',
  'prize.draw-requested.v1',
  'prize.draw-retried.v1',
  'prize.winner-drawn.v1',
  'prize.unawarded.v1',
  'prize.paid.v1',
  'forge.authorized.v1',
  'forge.purchased.v1',
  'forge.burned.v1',
  'forge.expired.v1',
  'forge.limits-set.v1',
  'forge.keeper-set.v1',
  'forge.paused.v1',
  'governance.proposed.v1',
  'governance.simulated.v1',
  'governance.approved.v1',
  'governance.reviewed.v1',
  'governance.scheduled.v1',
  'governance.executed.v1',
  'governance.retired.v1',
  'governance.cancelled.v1',
  'governance.journal-committed.v1',
  'governance.guardian-action.v1',
  'governance.role-set.v1',
  'config.engine-registered.v1',
  'config.formation-registered.v1',
  'config.formation-retired.v1',
  'config.registered.v1',
  'config.activated.v1',
  'config.admin-transferred.v1',
  'randomness.requested.v1',
  'randomness.fulfilled.v1',
  'liquidity.provided.v1',
  'liquidity.fees-collected.v1',
  'game.funding-paused.v1',
  'journal.entry.v1',
] as const;
export const FeedEventType = z.enum(FEED_EVENT_TYPES);
export type FeedEventType = z.infer<typeof FeedEventType>;

/** Non-persisted SSE control events (no `id:` line, so they never move the resume cursor). */
export const STREAM_CONTROL_TYPES = ['stream.hello.v1', 'stream.status.v1'] as const;
export const StreamHelloDto = z.object({
  latestSeq: z.number(),
  resumeFrom: z.number(),
  heads: z.object({ indexed: Dec.nullable(), latest: Dec.nullable(), safe: Dec.nullable(), finalized: Dec.nullable() }),
});

export const ErrorDto = z.object({ error: z.object({ code: z.string(), message: z.string() }) });

/** Integer parameter accepted as JSON number or decimal string (large values must be strings). */
export const IntLike = z.union([z.number().int(), Dec]);

export const HealthDto = z.object({
  ok: z.boolean(),
  service: z.literal('runner-api'),
  version: z.string(),
  chainId: z.number(),
  time: z.string(),
  rpc: z.object({ reachable: z.boolean(), latestBlock: Dec.nullable(), error: z.string().nullable() }),
  indexer: z.object({
    headBlock: Dec.nullable(),
    lagBlocks: z.number().nullable(),
    updatedAt: z.string().nullable(),
    stale: z.boolean(),
    reorgs: z.number(),
  }),
  executor: z.object({ reachable: z.boolean(), detail: z.string() }),
});

/**
 * LOCAL/TEST economic profile a deployment was made with (DeployLocal `RUNNER_ECON_PROFILE`,
 * contracts/script/EconomicProfiles.sol, mirrored by @runner/simulation profiles.ts). There is no "approved" status:
 * 'unknown' means the manifest names a profile this build does not know.
 */
export const EconomicProfileStatus = z.enum(['local-fixture', 'study-candidate', 'unknown']);
export const EconomicProfileDto = z.object({
  id: z.string(),
  label: z.string(),
  status: EconomicProfileStatus,
  /** Always false: no economic profile is approved for production. */
  approvedForProduction: z.literal(false),
  /** Caveat shown with the label, e.g. "LOCAL/TEST only, not approved for production". */
  note: z.string(),
});
export type EconomicProfileDto = z.infer<typeof EconomicProfileDto>;

export const DeploymentResponse = Deployment.extend({
  source: Source,
  manifestHash: Hash,
  randomnessProvider: z.object({ address: Addr, kind: z.string(), isTest: z.boolean(), label: z.string() }),
  /** null when the deployment is not a local/test deployment made with a profile (e.g. a non-local manifest). */
  economicProfile: EconomicProfileDto.nullable(),
});

export const RunResponse = RunDto.extend({ source: Source });

export const StepsResponse = z.object({
  source: Source,
  runId: z.number(),
  steps: z.array(StepDto),
  nextCursor: z.number().nullable(),
});

export const SwapDto = z.object({
  window: z.number(),
  periodIndex: z.number(),
  sender: Addr,
  participant: Addr.nullable(),
  isBuy: z.boolean(),
  runnerDelta: Dec,
  quoteAmount: Dec,
  fee: Dec,
  txHash: Hash,
  blockNumber: Dec,
  logIndex: z.number(),
  finality: Finality,
});

export const WindowResponse = z.object({
  source: Source,
  window: z.number(),
  startsAt: z.number(),
  endsAt: z.number(),
  closed: z.boolean(),
  input: WindowInputDto,
  consumedBy: z.object({ runId: z.number(), step: z.number(), resolved: z.boolean() }).nullable(),
  swaps: z.array(SwapDto),
  indexedMatchesChain: z.boolean(),
});

export const CustodyCheckDto = z.object({
  holder: z.string(),
  address: Addr,
  asset: z.string(),
  balance: Dec,
  obligations: Dec,
  surplus: Dec,
  ok: z.boolean(),
});

/**
 * Accrual epoch status in the waterfall: `open` (the hook's current period, still receiving fees), `closed-unsettled`
 * (its epoch has ended but RunnerTreasury.settle has not split it yet: period index ≥ nextPeriod) or `settled`
 * (indexed PeriodSettled, split once under the epoch's policy).
 */
export const WaterfallEpochStatus = z.enum(['open', 'closed-unsettled', 'settled']);
export type WaterfallEpochStatus = z.infer<typeof WaterfallEpochStatus>;

/**
 * One accrual epoch. `settled` rows come from indexed PeriodSettled events and keep their split. `open` and
 * `closed-unsettled` rows come from contract views at the pinned block (RunnerHook.currentPeriodIndex / period(i),
 * RunnerTreasury.nextPeriod / policyIdAt) and have no split yet (null): R is split exactly once, at settlement. An
 * `open` row with `periodIndex: null` is the current epoch before its first fee (the hook opens a period on the first
 * swap of an epoch, so nothing has accrued yet).
 */
export const WaterfallEpochDto = z.object({
  status: WaterfallEpochStatus,
  periodIndex: z.number().nullable(),
  epoch: z.number(),
  /** Settled: the policy it was split with. Open/unsettled: the policy scheduled for that epoch (policyIdAt). */
  policyId: z.number(),
  accrued: Dec,
  holdersDirect: Dec.nullable(),
  holdersDeferred: Dec.nullable(),
  forge: Dec.nullable(),
  road: Dec.nullable(),
  prize: Dec.nullable(),
  operations: Dec.nullable(),
  /** Settlement transaction (settled rows only). */
  txHash: Hash.nullable(),
  /** Settled: the settlement block. Open/unsettled: the pinned block the period was read at. */
  blockNumber: Dec,
  finality: Finality,
});
export type WaterfallEpochDto = z.infer<typeof WaterfallEpochDto>;

export const EscrowedRunDto = RunEscrowDto.extend({ runId: z.number() });

/**
 * Provenance of road money (indexed): `settled` road shares and `sponsored` road donations flow in, `reserved` run
 * budgets flow out (RunFunded, with the run's sealed cohort and policy), and `returned` carries what a closed run did
 * not assign back to the reserve (RunClosed) for later runs.
 */
export const RoadLedgerEntryDto = z.object({
  kind: z.enum(['settled', 'sponsored', 'reserved', 'returned']),
  amount: Dec,
  runId: z.number().nullable(),
  epoch: z.number().nullable(),
  policyId: z.number().nullable(),
  cohortEpoch: z.number().nullable(),
  from: Addr.nullable(),
  txHash: Hash,
  blockNumber: Dec,
  at: z.number(),
  finality: Finality,
});

/**
 * Treasury custody and the five-way waterfall. Reserves, escrows and backing are accounting obligations; nothing
 * here is a price floor, redemption price or yield.
 */
export const TreasuryResponse = z.object({
  source: Source,
  quoteSymbol: z.string(),
  quoteDecimals: z.number(),
  /** Cash-backed buckets held by RunnerTreasury (raw quote units). */
  buckets: z.object({
    operations: Dec,
    prizeReserve: Dec,
    forgeReserve: Dec,
    roadReserve: Dec,
    executionReserve: Dec,
    lpIncome: Dec,
    /** Unassigned budgets of open runs. */
    runEscrowed: Dec,
    /** Holder-directed road value waiting for its cohort to close. */
    cohortPending: Dec,
    /** Holder allocation of the epoch being flushed (not yet transferred to RunnerRewards). */
    pendingDirect: Dec,
    pendingDeferred: Dec,
    /** Holder credits queued because the quote asset refused the transfer to RunnerRewards (D43). */
    holderCreditsQueued: Dec,
  }),
  /** Accrued, unredeemed hook fees: a receivable, not funds. */
  receivable: Dec,
  accountedCash: Dec,
  balance: Dec,
  solvent: z.boolean(),
  /**
   * Holder credits waiting in the treasury, oldest first (RunnerTreasury.queuedHolderCredits; D43). Non-empty only while
   * the quote asset refuses transfers into RunnerRewards; every later settle retries up to 16 in order, and reward epochs
   * are never finalised past the oldest one. `epochs` lists at most 50. `count` is null when the (unbounded) list view
   * could not be read; `queued` is always exact.
   */
  holderCredits: z.object({
    queued: Dec,
    count: z.number().nullable(),
    oldestEpoch: z.number().nullable(),
    epochs: z.array(z.object({ epoch: z.number(), direct: Dec, deferred: Dec })),
  }),
  totals: z.object({
    accrued: Dec,
    redeemed: Dec,
    settled: Dec,
    toRewards: Dec,
    toRunCohorts: Dec,
    prizeReserved: Dec,
    prizeReturned: Dec,
    forgeAuthorized: Dec,
    forgeReturned: Dec,
    opsSpent: Dec,
    roadReserved: Dec,
    roadToHolders: Dec,
    roadToForge: Dec,
    roadReturned: Dec,
  }),
  waterfall: z.object({
    /** Policy scheduled for the current epoch. */
    policy: WaterfallPolicyDto,
    /** Sum over settled accrual epochs (indexed PeriodSettled). */
    totals: WaterfallSplitDto.extend({ accrued: Dec, epochs: z.number() }),
    /**
     * Newest first: the open epoch, then closed-but-unsettled epochs (contract views; at most 50 listed), then settled
     * epochs (indexed; at most 50). Only `settled` rows have a split.
     */
    epochs: z.array(WaterfallEpochDto),
    /**
     * Not yet split (contract views): `periods` = accrual periods from RunnerTreasury.nextPeriod through the hook's
     * current period (the open one included), `closedPeriods` = those whose epoch has ended, `accrued` =
     * RunnerHook.totalAccrued − RunnerTreasury.totalSettled (fees accrued in every unsettled period).
     */
    unsettled: z.object({ periods: z.number(), closedPeriods: z.number(), listed: z.number(), accrued: Dec }),
  }),
  road: z.object({
    reserve: Dec,
    runEscrowed: Dec,
    executionReserve: Dec,
    openRun: EscrowedRunDto.nullable(),
    /**
     * RunnerTreasury cohort views: `epoch`/`openRuns` describe the OLDEST pending cohort, `pending` is the holder-directed
     * road value pending across all cohorts not yet credited.
     */
    cohort: z.object({ active: z.boolean(), epoch: z.number(), openRuns: z.number(), pending: Dec }),
    runFinalizedCount: z.number(),
    /** Most recent road-money movements first (indexed). */
    ledger: z.array(RoadLedgerEntryDto),
  }),
  /** Tagged sponsorship (RunnerTreasury.donate): never fee revenue. */
  sponsorship: z.object({
    total: Dec,
    byBucket: z.object({ prize: Dec, forge: Dec, road: Dec, operations: Dec }),
    note: z.string(),
  }),
  /** Runner identity backing: part of the vault's principal, a liability to token owners — not a price. */
  identity: z.object({
    backing: Dec,
    cap: z.number(),
    minted: z.number(),
    live: z.number(),
    principalLiability: Dec,
    /** live × backing; must equal principalLiability. */
    expectedLiability: Dec,
    consistent: z.boolean(),
    vaultPrincipal: Dec,
    ordinaryPrincipal: Dec,
    strayRunner: Dec,
  }),
  finalizedEpochs: z.number(),
  nextPeriod: z.number(),
  currentPeriod: z.number(),
  custody: z.array(CustodyCheckDto),
  note: z.string(),
});
export type TreasuryResponse = z.infer<typeof TreasuryResponse>;

export const ShoeEntryDto = z.object({ index: z.number(), participant: Addr, weight: Dec, cumWeight: Dec });

export const ShoeResponse = ShoeDto.extend({
  source: Source,
  entries: z.array(ShoeEntryDto),
  entriesOffset: z.number(),
  nextCursor: z.number().nullable(),
  history: z.array(z.object({ event: z.string(), txHash: Hash, blockNumber: Dec, finality: Finality })),
  draw: z
    .object({
      requestId: Dec,
      randomness: Hash.nullable(),
      ticket: Dec.nullable(),
      expectedWinner: Addr.nullable(),
      verified: z.boolean().nullable(),
      detail: z.string(),
    })
    .nullable(),
});

export const BuybackResponse = BuybackDto.extend({
  source: Source,
  keeper: Addr,
  authorizationCount: z.number(),
  periodStart: z.number(),
  periodSpent: Dec,
  limits: z.object({
    perTradeCap: Dec,
    periodCap: Dec,
    periodSeconds: z.number(),
    twapSeconds: z.number(),
    maxDeviationTicks: z.number(),
    maxSlippageBps: z.number(),
  }),
});

export const FormationDto = z.object({
  id: z.number(),
  hash: Hash,
  length: z.number(),
  mirrorable: z.boolean(),
  maxShift: z.number(),
  retired: z.boolean(),
  objects: z.array(z.object({ kind: z.number(), kindName: z.string(), y: z.number(), xL: z.number(), xR: z.number(), param: z.number() })),
});

export const ConfigResponse = z.object({
  source: Source,
  activeVersion: z.number(),
  configCount: z.number(),
  version: z.number(),
  previewWindows: z.number(),
  /** Engine pinned by this configuration (and by every run that snapshots it). */
  engine: z.object({ engineId: z.number(), address: Addr, version: z.number(), codeHash: Hash }),
  movement: z.record(z.string(), Dec),
  handlers: z.record(z.string(), z.union([Dec, z.boolean()])),
  /** Road budget parameters (raw quote units / bps). */
  road: z.record(z.string(), Dec),
  /** Waterfall policy in force for the current epoch (configurations do not carry economics). */
  waterfall: WaterfallPolicyDto,
  formationIds: z.array(z.number()),
  formations: z.array(FormationDto),
  bounds: z.record(z.string(), z.string()),
});

export const ProposalsResponse = z.object({
  source: Source,
  total: z.number(),
  proposals: z.array(ProposalDto),
  nextCursor: z.number().nullable(),
});

export const ProposalResponse = ProposalDto.extend({
  source: Source,
  payload: z.string(),
  history: z.array(z.object({ event: z.string(), txHash: Hash, blockNumber: Dec, finality: Finality, args: z.record(z.string(), z.unknown()) })),
});

export const JournalEntryWithSourceDto = JournalEntryDto.extend({ blockNumber: Dec, finality: Finality, template: z.string() });

export const JournalResponse = z.object({
  source: Source,
  entries: z.array(JournalEntryWithSourceDto),
  nextCursor: z.string().nullable(),
  drafting: z.object({ provider: z.string(), note: z.string() }),
});

export const EventsResponse = z.object({
  source: Source,
  events: z.array(FeedEventDto),
  nextCursor: z.number(),
  latestSeq: z.number(),
});

export const ReplayRoadEffectDto = z.object({ step: z.number(), effect: z.number(), requested: Dec, assigned: Dec });
export const ReplayEscrowDto = z.object({ budget: Dec, assigned: Dec, toHolders: Dec, toForge: Dec, open: z.boolean() });
export const ReplayModifierDto = z.object({ id: z.number(), startStep: z.number().nullable(), endReason: z.number().nullable() });

export const ReplayResponse = ReplayDto.extend({
  source: Source,
  configVersion: z.number(),
  /** Engine pinned by the run (RunnerGame.run.engine / engineVersion); the replay uses its rules. */
  engineAddress: Addr.nullable(),
  engineVersion: z.number(),
  rules: z.number(),
  abandoned: z.boolean(),
  budget: Dec,
  courseRecomputedMatches: z.boolean(),
  courseHashOnchain: Hash.nullable(),
  courseHashIndexed: Hash.nullable(),
  inputMismatches: z.array(z.object({ step: z.number(), window: z.number(), indexedNet: Dec, stepNet: Dec, indexedVolume: Dec, stepVolume: Dec })),
  complete: z.boolean(),
  road: z.object({
    /** Indexed RunnerGame.RoadEffect events of the run, in order. */
    effectsOnchain: z.array(ReplayRoadEffectDto),
    /** Road effects of the reference engine replayed with the run's budget. */
    effectsEngine: z.array(ReplayRoadEffectDto),
    effectsMatch: z.boolean(),
    /** RunnerTreasury.runEscrow(runId) at the pinned block. */
    escrowOnchain: ReplayEscrowDto,
    /** The engine's road state after the replayed steps. */
    escrowEngine: ReplayEscrowDto,
    /** null while the replay is incomplete (the indexer is behind the pinned block): a partial replay cannot be
     *  compared with the treasury's current escrow. */
    escrowMatches: z.boolean().nullable(),
    exitRouteOnchain: RouteName.nullable(),
    exitRouteEngine: RouteName.nullable(),
    returnedOnchain: Dec.nullable(),
    returnedEngine: Dec.nullable(),
  }),
  modifiers: z.object({ onchain: z.array(ReplayModifierDto), engine: z.array(ReplayModifierDto), match: z.boolean() }),
});
export type ReplayResponse = z.infer<typeof ReplayResponse>;

export const MarketResponse = MarketDto;

export const QuoteResponse = QuoteDto.extend({
  quoter: Addr,
  midPriceQuotePerRunner: z.string(),
  executionPriceQuotePerRunner: z.string(),
  feeUnits: z.literal('quote raw units'),
  note: z.string(),
});

export const OperatorNonceRequest = z.object({ address: Addr });
export const OperatorNonceResponse = z.object({
  nonce: z.string(),
  message: z.string(),
  domain: z.string(),
  chainId: z.number(),
  expiresAt: z.string(),
});
export const OperatorSessionRequest = z.object({ message: z.string().min(1).max(2000), signature: z.string().regex(/^0x[0-9a-fA-F]*$/).max(20_000) });
export const OperatorSessionResponse = z.object({ token: z.string(), address: Addr, roles: z.array(z.string()), expiresAt: z.string() });

export const MovementParamsInput = z.object({
  basePace: IntLike,
  paceBonusMax: IntLike,
  paceBonusVolumePpm: IntLike,
  xMax: IntLike,
  halfWidth: IntLike,
  uMax: IntLike,
  sensitivity: IntLike,
  decayPpm: IntLike,
  vxMax: IntLike,
  forkMedian: IntLike,
  stumbleDistance: IntLike,
});
export const HandlerParamsInput = z.object({
  barrierSlowPct: IntLike,
  barrierSlowSteps: IntLike,
  overheatSlowPct: IntLike,
  overheatSteps: IntLike,
  shoePrize: IntLike,
  shoeMinPrize: IntLike,
  forgeGateMax: IntLike,
  forgeAuthSeconds: IntLike,
  finishReleasesDeferred: z.boolean(),
});
export const RoadParamsInput = z.object({
  runBudget: IntLike,
  minRunBudget: IntLike,
  opsProvision: IntLike,
  waterSliceBps: IntLike,
  overheatSliceBps: IntLike,
  overheatBiasSteps: IntLike,
  overheatCap: IntLike,
});
export const WaterfallPolicyInput = z.object({
  holdersBps: IntLike,
  forgeBps: IntLike,
  roadBps: IntLike,
  prizeBps: IntLike,
  opsBps: IntLike,
  holderDeferredBps: IntLike,
});

/** Preview-only. Nothing is signed or broadcast. */
export const SimulateConfigRequest = z.object({
  movement: MovementParamsInput,
  handlers: HandlerParamsInput,
  road: RoadParamsInput,
  /** Registered engine id the configuration would pin (default: the active configuration's engine). */
  engineId: z.number().int().positive().optional(),
  formations: z.array(z.number().int().positive()).min(1).max(6).optional(),
  previewWindows: z.number().int().optional(),
  seeds: z.number().int().min(1).max(8).optional(),
});

export const SimulatedRunRoadDto = z.object({ budget: Dec, toHolders: Dec, toForge: Dec, returned: Dec, effects: z.number(), overheatBiases: z.number() });

export const SimulateConfigResponse = z.object({
  previewOnly: z.literal(true),
  valid: z.boolean(),
  reason: z.string().nullable(),
  engineVersion: z.number().nullable(),
  formations: z.array(z.number()),
  runs: z.array(
    z.object({
      seed: Hash,
      strategy: z.string(),
      steps: z.number(),
      finished: z.boolean(),
      reachedFinish: z.boolean(),
      collisions: z.object({ barrier: z.number(), burningBarricade: z.number(), puddle: z.number() }),
      shoe: z.enum(['collected', 'missed', 'absent']),
      route: RouteName,
      forgeGates: z.number(),
      waterStations: z.number(),
      /** The run's reserved road budget (road.runBudget) and where the engine sent it. */
      road: SimulatedRunRoadDto,
    }),
  ),
  summary: z.object({
    runs: z.number(),
    collisions: z.number(),
    collisionsPerRun: z.number(),
    shoeCollectionRate: z.number().nullable(),
    finishRate: z.number(),
    /** Fractions of the summed run budgets (0..1). */
    roadToHoldersShare: z.number(),
    roadToForgeShare: z.number(),
    roadReturnedShare: z.number(),
  }),
});

/** Preview-only waterfall arithmetic (WaterfallLib.split mirror) with an optional hypothetical run of the road budget. */
export const SimulateWaterfallRequest = z.object({
  /** Net project revenue R of one epoch (raw quote units). */
  revenue: IntLike,
  /** Default: the policy scheduled for the current epoch on chain. */
  policy: WaterfallPolicyInput.optional(),
  /** A hypothetical run funded from this epoch's road bucket (applied in this order: overheat steps, water hits, exit). */
  run: z
    .object({
      /** Default: the whole road bucket of `revenue`. */
      budget: IntLike.optional(),
      waterHits: z.number().int().min(0).max(8).default(0),
      overheatSteps: z.number().int().min(0).max(6).default(0),
      exit: z.enum(['harvest', 'forge', 'neutral', 'none']).default('none'),
      /** Defaults: the active configuration's road slices; cap scaled to the budget (overheatCap / runBudget). */
      waterSliceBps: IntLike.optional(),
      overheatSliceBps: IntLike.optional(),
      overheatCapBps: IntLike.optional(),
    })
    .optional(),
  /** Labelled comparison with the superseded specification-3.0 allocation (analysis only). */
  compare: z.array(z.literal('LEGACY_V3')).max(1).optional(),
});

export const SimulateWaterfallResponse = z.object({
  previewOnly: z.literal(true),
  profile: z.string(),
  policy: WaterfallPolicyDto.omit({ policyId: true, fromEpoch: true }).extend({ policyId: z.number().nullable(), fromEpoch: z.number().nullable() }),
  revenue: Dec,
  split: WaterfallSplitDto,
  run: z
    .object({
      budget: Dec,
      water: Dec,
      overheat: Dec,
      exitToHolders: Dec,
      exitToForge: Dec,
      returnedToRoad: Dec,
      /** Road leftover of the bucket not reserved by the hypothetical run. */
      roadNotReserved: Dec,
    })
    .nullable(),
  /** Where R ends up: base holders + road to holders, base Forge + road to Forge, prize, operations, road still unassigned. */
  outcome: z.object({ holders: Dec, forge: Dec, prize: Dec, operations: Dec, roadUnassigned: Dec }),
  legacy: z
    .object({
      label: z.string(),
      points: z.array(z.object({ x: z.number(), operations: Dec, prize: Dec, direct: Dec, forge: Dec, deferred: Dec })),
    })
    .nullable(),
});

// =====================================================================================================
// Runner identities, collection history, owner achievements and versions (change order 4.0 §4–§7)
// =====================================================================================================

export const IdentityStatus = z.enum(['live', 'redeemed', 'converted']);
export const IdentityOrigin = z.enum(['deposit', 'convert']);

export const IdentityTraitsDto = z.object({
  /** Base trait indices (frame, eyes, headband, accent, field, singlet). */
  values: z.array(z.number()),
  labels: z.object({ frame: z.string(), eyes: z.string(), headband: z.string(), accent: z.string(), field: z.string(), singlet: z.string() }),
  seed: Hash,
  /** TS derivation (packages/simulation identity.ts) equals RunnerIdentity.baseTraits on chain. Null when burned (not readable). */
  verifiedOnchain: z.boolean().nullable(),
});

export const IdentitySummaryDto = z.object({
  tokenId: Dec,
  status: IdentityStatus,
  /** Current owner (null once burned). */
  owner: Addr.nullable(),
  backing: Dec,
  /** First epoch the CURRENT holder's weight counts (resets to transfer epoch + 1 on every transfer). */
  eligibleFrom: z.number(),
  origin: IdentityOrigin,
  mintedAt: z.number(),
  mintedBlock: Dec,
  transfers: z.number(),
  traits: IdentityTraitsDto.shape.labels,
});
export type IdentitySummaryDto = z.infer<typeof IdentitySummaryDto>;

export const IdentityCollectionDto = z.object({
  address: Addr,
  name: z.string(),
  symbol: z.string(),
  backing: Dec,
  cap: z.number(),
  minted: z.number(),
  live: z.number(),
  intakeOpen: z.boolean(),
  intakePaused: z.boolean(),
  principalLiability: Dec,
  cosmeticRenderer: Addr.nullable(),
  baseRenderer: Addr,
});

export const IdentitiesResponse = z.object({
  source: Source,
  collection: IdentityCollectionDto,
  identities: z.array(IdentitySummaryDto),
  nextCursor: z.string().nullable(),
});

/** One entry of a token's collectible history (follows the token across owners). */
export const IdentityEventDto = z.object({
  event: z.enum(['minted', 'transferred', 'equipped', 'redeemed', 'converted']),
  from: Addr.nullable(),
  to: Addr.nullable(),
  epoch: z.number().nullable(),
  detail: z.record(z.string(), z.unknown()),
  txHash: Hash,
  blockNumber: Dec,
  at: z.number(),
  finality: Finality,
});

export const WardrobeItemDto = z.object({ itemId: z.number(), slot: z.number(), slotName: z.string(), name: z.string(), artHash: Hash });

export const IdentityResponse = z.object({
  source: Source,
  tokenId: Dec,
  status: IdentityStatus,
  owner: Addr.nullable(),
  backing: Dec,
  eligibleFrom: z.number(),
  mintedEpoch: z.number(),
  /** The current holder's backing counts in the current epoch. */
  eligibleNow: z.boolean(),
  origin: IdentityOrigin.nullable(),
  traits: IdentityTraitsDto,
  /** RunnerIdentity.tokenURI (data: URI from the cosmetic renderer, else the immutable base renderer). Null once burned. */
  metadataUri: z.string().nullable(),
  renderer: z.object({ cosmetic: Addr.nullable(), base: Addr }),
  /** Equipment of the CURRENT owner only (wardrobe keyed by token and owner). */
  equipment: z.object({
    owner: Addr.nullable(),
    slots: z.array(z.object({ slot: z.number(), slotName: z.string(), item: WardrobeItemDto.nullable() })),
  }),
  history: z.array(IdentityEventDto),
  /** Ownership segments: whose weight the backing was, from which epoch (inclusive) until which epoch (exclusive). */
  holdings: z.array(z.object({ owner: Addr, eligibleFrom: z.number(), untilEpoch: z.number().nullable() })),
  /** Runs whose sealed cohort counted this backing (indexed at funding), with the holder at the time. */
  participation: z.array(z.object({ runId: z.number(), cohortEpoch: z.number(), owner: Addr })),
});

export const AccountRunnersResponse = z.object({
  source: Source,
  account: Addr,
  owned: z.array(IdentitySummaryDto),
  /** Runners this account held before (history stays with the token; this is only the list). */
  previouslyOwned: z.array(IdentitySummaryDto),
  identityPrincipal: z.object({ eligible: Dec, pending: Dec, total: Dec }),
});

export const AchievementKind = z.enum(['runner-in-cohort', 'run-steered', 'forge-gate', 'shoe-collected', 'shoe-won']);

/** Owner achievements stay with the participant's address (they never move with a token). */
export const AchievementDto = z.object({
  kind: AchievementKind,
  runId: z.number().nullable(),
  tokenId: Dec.nullable(),
  shoeId: Dec.nullable(),
  step: z.number().nullable(),
  txHash: Hash,
  blockNumber: Dec,
  at: z.number(),
  finality: Finality,
});

export const AccountAchievementsResponse = z.object({
  source: Source,
  account: Addr,
  summary: z.object({ runsWithRunner: z.number(), runsSteered: z.number(), forgeGatesPassed: z.number(), shoesCollected: z.number(), shoesWon: z.number() }),
  achievements: z.array(AchievementDto),
  nextCursor: z.string().nullable(),
  definitions: z.record(AchievementKind, z.string()),
});

export const EngineVersionDto = z.object({
  engineId: z.number(),
  address: Addr,
  version: z.number(),
  codeHash: Hash,
  registeredAt: z.number().nullable(),
  txHash: Hash.nullable(),
  /** Configuration versions pinning this engine. */
  configs: z.array(z.number()),
});

export const ConfigVersionDto = z.object({
  version: z.number(),
  engineId: z.number(),
  engineVersion: z.number(),
  engineAddress: Addr,
  hash: Hash.nullable(),
  active: z.boolean(),
  registeredAt: z.number().nullable(),
  activatedAt: z.number().nullable(),
  previewWindows: z.number(),
  formations: z.array(z.number()),
});

export const PolicyVersionDto = WaterfallPolicyDto.extend({
  status: z.enum(['current', 'scheduled', 'historical']),
  scheduledAt: z.number().nullable(),
  txHash: Hash.nullable(),
  /** Earlier, never-effective schedules of the same id that a later schedule replaced. */
  replaced: z.array(WaterfallPolicyDto.extend({ scheduledAt: z.number(), txHash: Hash })),
});

export const ReleaseDto = ProposalDto.extend({ class: z.enum(['economic', 'cosmetic', 'record']) });

export const VersionsResponse = z.object({
  source: Source,
  currentEpoch: z.number(),
  engines: z.array(EngineVersionDto),
  configs: z.object({ active: z.number(), count: z.number(), items: z.array(ConfigVersionDto) }),
  policies: z.object({ current: PolicyVersionDto, scheduled: z.array(PolicyVersionDto), historical: z.array(PolicyVersionDto) }),
  releases: z.object({ economic: z.array(ReleaseDto), cosmetic: z.array(ReleaseDto), record: z.array(ReleaseDto) }),
  governance: z.object({ delay: z.number(), minDelay: z.number(), note: z.string() }),
  cosmetics: z.object({ renderer: Addr.nullable(), baseRenderer: Addr, items: z.array(WardrobeItemDto) }),
});

export type AchievementDto = z.infer<typeof AchievementDto>;
export type IdentitiesResponse = z.infer<typeof IdentitiesResponse>;
export type IdentityResponse = z.infer<typeof IdentityResponse>;
export type AccountRunnersResponse = z.infer<typeof AccountRunnersResponse>;
export type AccountAchievementsResponse = z.infer<typeof AccountAchievementsResponse>;
export type VersionsResponse = z.infer<typeof VersionsResponse>;
export type SimulateConfigResponse = z.infer<typeof SimulateConfigResponse>;
export type SimulateWaterfallResponse = z.infer<typeof SimulateWaterfallResponse>;
export type RoadLedgerEntryDto = z.infer<typeof RoadLedgerEntryDto>;
export type ConfigResponse = z.infer<typeof ConfigResponse>;

// Collection manifest: canonical schema + the validation shared by the API and the web Operator page.
export * from './manifest.js';
